import React, { useEffect, useRef, useState, useCallback } from "react";
import { Play, Square, Maximize2, Minimize2 } from "lucide-react";
import Layout from "../components/Layout";
import useFitText from "../hooks/useFitText";
import { useLanguage } from "../i18n/LanguageContext";

const COLORS = [
  { name: "red", hex: "#ff0000", text: "#000000" },
  { name: "blue", hex: "#0040ff", text: "#000000" },
  { name: "yellow", hex: "#ffe600", text: "#000000" },
  { name: "green", hex: "#00d000", text: "#000000" },
];

function pickRandom(prev) {
  const color = COLORS[Math.floor(Math.random() * COLORS.length)];
  const num = Math.floor(Math.random() * 10) + 1;
  const candidate = { color, num };
  if (
    prev &&
    candidate.color.name === prev.color.name &&
    candidate.num === prev.num
  ) {
    return pickRandom(prev);
  }
  return candidate;
}

export default function ColorNumberTrainer() {
  const { t } = useLanguage();
  const [running, setRunning] = useState(false);
  const [current, setCurrent] = useState(null);
  const [intervalMs, setIntervalMs] = useState(1000);
  const [count, setCount] = useState(0);
  const [isFs, setIsFs] = useState(false);
  const intervalRef = useRef(null);
  const stageWrapRef = useRef(null);

  const { containerRef, textRef } = useFitText([current, isFs]);

  const toggleRun = useCallback(() => setRunning((r) => !r), []);

  useEffect(() => {
    if (!running) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
      return undefined;
    }
    setCount(0);
    let prev = null;
    const fire = () => {
      const next = pickRandom(prev);
      prev = next;
      setCurrent(next);
      setCount((c) => c + 1);
    };
    fire();
    intervalRef.current = setInterval(fire, intervalMs);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [running, intervalMs]);

  const toggleFs = async () => {
    const el = stageWrapRef.current;
    if (!el) return;
    try {
      if (!document.fullscreenElement) {
        await el.requestFullscreen();
        setIsFs(true);
      } else {
        await document.exitFullscreen();
        setIsFs(false);
      }
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    const onChange = () => setIsFs(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  return (
    <Layout showBack>
      <div className="vls-page vls-page-trainer" data-testid="colors-numbers-page">
        {!running && (
          <h1 className="vls-page-title">
            <span className="vls-grad-cyan">{t("colorsNumbers")}</span>
          </h1>
        )}

        <div
          ref={stageWrapRef}
          className="vls-color-stage vls-stage-fit"
          data-testid="color-number-stage"
          style={{
            backgroundColor: running && current ? current.color.hex : "#0b0b0e",
            color: running && current ? current.color.text : "#ffffff",
          }}
        >
          <div className="vls-fit-box" ref={containerRef}>
            {running && current ? (
              <span
                ref={textRef}
                className="vls-fit-text vls-fit-num"
                data-testid="big-number"
              >
                {current.num}
              </span>
            ) : (
              <div className="vls-stage-placeholder">
                <p>{t("tapToStart")}</p>
              </div>
            )}
          </div>
          <button
            type="button"
            className="vls-fs-btn"
            onClick={toggleFs}
            aria-label={isFs ? t("exitFullscreen") : t("fullscreen")}
            data-testid="fullscreen-button"
          >
            {isFs ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>
        </div>

        {!running && (
          <div className="vls-interval-row">
            <label className="vls-interval-label" htmlFor="interval-slider">
              {t("interval")}:{" "}
              <strong data-testid="interval-value">
                {(intervalMs / 1000).toFixed(1)}s
              </strong>
            </label>
            <input
              id="interval-slider"
              type="range"
              min="300"
              max="3000"
              step="100"
              value={intervalMs}
              onChange={(e) => setIntervalMs(Number(e.target.value))}
              data-testid="interval-slider"
            />
          </div>
        )}

        <div className="vls-stats">
          <div className="vls-stat">
            <span className="vls-stat-label">{t("tasksLabel")}</span>
            <span className="vls-stat-value" data-testid="stat-count">{count}</span>
          </div>
        </div>

        <div className="vls-controls">
          <button
            type="button"
            className={`vls-btn-primary ${running ? "stop" : ""}`}
            onClick={toggleRun}
            data-testid="start-stop-button"
          >
            {running ? <Square size={22} /> : <Play size={22} />}
            <span>{running ? t("stop") : t("start")}</span>
          </button>
        </div>
      </div>
    </Layout>
  );
}
